import { useId } from "react";
import { useJourney } from "../../../hooks/useJourney";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import { journeyEnumToCatalogType } from "../../../lib/onboarding/journeyEnumCatalog";
import { toEnum } from "../../../lib/onboarding/onboardingVocabulary";
import type { JourneyType } from "../../../types/journey";
import type { OnboardingAnswers } from "../../../types/onboarding";
import styles from "./JourneyStepsQuestion.module.css";

interface JourneyStepsQuestionProps {
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | string[]) => void;
}

// Pure check-cascade helper (contract §4). Checking a step auto-checks all
// prior steps in that journey; unchecking removes only the targeted step.
// Result keeps this journey's entries in ascending catalog order so the
// highest-order checked entry is always last ("current step").
export function toggleStep(
	checkedIds: string[],
	prefix: string,
	orderedStepIds: string[],
	stepId: string,
): string[] {
	const key = (id: string) => `${prefix}:${id}`;
	const mine = new Set(checkedIds.filter((c) => c.startsWith(`${prefix}:`)));
	const others = checkedIds.filter((c) => !c.startsWith(`${prefix}:`));
	if (mine.has(key(stepId))) {
		mine.delete(key(stepId));
	} else {
		const idx = orderedStepIds.indexOf(stepId);
		for (let i = 0; i <= idx; i++) mine.add(key(orderedStepIds[i]));
	}
	const rebuilt = orderedStepIds.filter((id) => mine.has(key(id))).map(key);
	return [...others, ...rebuilt];
}

interface ResolvedJourney {
	displayLabel: string;
	catalogType: JourneyType;
}

export function JourneyStepsQuestion({
	answers,
	onInputChange,
}: JourneyStepsQuestionProps) {
	const selectedJourneys = Array.isArray(answers.fertilityJourneyTypes)
		? answers.fertilityJourneyTypes
		: [];

	const statusEnum = toEnum(
		"fertilityStatus",
		String(answers.fertilityStatus ?? ""),
	);
	const roleEnum = toEnum("fertilityRole", String(answers.fertilityRole ?? ""));

	// Two branches skip S7 yet still show steps (FigJam): currently_pregnant →
	// pregnancy catalog; surrogate role → surrogate-branch catalog. The pill
	// label refines to journey.name once the catalog loads.
	const resolved: ResolvedJourney[] =
		statusEnum === "currently_pregnant"
			? [{ displayLabel: "Pregnancy", catalogType: "pregnancy" }]
			: roleEnum === "surrogate"
				? [{ displayLabel: "Surrogacy", catalogType: "surrogacy_surrogate" }]
				: selectedJourneys.flatMap((label) => {
						const journeyEnum = toEnum("journeyType", label) ?? label;
						const catalogType = journeyEnumToCatalogType(journeyEnum);
						return catalogType ? [{ displayLabel: label, catalogType }] : [];
					});

	const checkedIds = Array.isArray(answers.journeyCheckedStepIds)
		? answers.journeyCheckedStepIds
		: [];

	return (
		<div className={styles.container} data-testid="journey-steps-options">
			{resolved.map((journey) => (
				<JourneySection
					key={journey.catalogType}
					catalogType={journey.catalogType}
					displayLabel={journey.displayLabel}
					checkedIds={checkedIds}
					onChange={(next) => onInputChange("journeyCheckedStepIds", next)}
				/>
			))}
		</div>
	);
}

interface JourneySectionProps {
	catalogType: JourneyType;
	displayLabel: string;
	checkedIds: string[];
	onChange: (next: string[]) => void;
}

function JourneySection({
	catalogType,
	displayLabel,
	checkedIds,
	onChange,
}: JourneySectionProps) {
	const { t, locale } = useClientTranslations("onboarding");
	const { logOnboarding } = useAnalytics();
	const groupId = useId();
	const { data: journey, isLoading } = useJourney(catalogType, {
		enabled: true,
		locale,
	});

	const label = journey?.name ?? displayLabel;
	const orderedSteps = journey
		? [...journey.steps].sort((a, b) => a.order - b.order)
		: [];
	const orderedStepIds = orderedSteps.map((step) => step.id);

	return (
		<div className={styles.section}>
			<h2 className={styles.heading}>{label}</h2>

			{isLoading && (
				<div className={styles.loading} data-testid="journey-steps-loading">
					{t("journeySteps.loading")}
				</div>
			)}

			{!isLoading && journey && (
				<div className={styles.steps}>
					{orderedSteps.map((step) => {
						const checked = checkedIds.includes(`${catalogType}:${step.id}`);
						const inputId = `${groupId}-${step.id}`;
						return (
							<label key={step.id} className={styles.stepRow} htmlFor={inputId}>
								<input
									id={inputId}
									type="checkbox"
									className={styles.visuallyHidden}
									checked={checked}
									onChange={() => {
										const next = toggleStep(
											checkedIds,
											catalogType,
											orderedStepIds,
											step.id,
										);
										onChange(next);
										const key = `${catalogType}:${step.id}`;
										logOnboarding("step_toggled", {
											journey_type: catalogType,
											step_id: step.id,
											checked: next.includes(key),
											checked_count: next.filter((id) =>
												id.startsWith(`${catalogType}:`),
											).length,
										});
									}}
								/>
								<span
									className={`${styles.checkbox} ${
										checked ? styles.checkboxChecked : ""
									}`}
								>
									{checked && (
										<svg
											width="12"
											height="9"
											viewBox="0 0 12 9"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
											className={styles.checkmark}
											role="img"
											aria-label="Selected"
										>
											<path
												d="M1 4.5L4.5 8L11 1"
												stroke="white"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									)}
								</span>
								<span className={styles.stepTitle}>{step.title}</span>
							</label>
						);
					})}
				</div>
			)}
		</div>
	);
}
